/**
 * SaveSystem - localStorage-based save/load for game state, preferences, and stats.
 * Covers tasks 5.14 (save/load), 9.14 (fix data corruption), 10.10 (local preferences),
 * 13.8 (localStorage edge cases), 15.4 (friend list local).
 */

export interface MatchRecord {
  date: string;
  playerScore: number;
  opponentScore: number;
  duration: number; // seconds
  difficulty: string;
  result: "win" | "loss" | "draw";
  shotsOnTarget: number;
  possession: number; // 0–100
}

export interface PlayerStats {
  gamesPlayed: number;
  wins: number;
  losses: number;
  draws: number;
  goalsScored: number;
  goalsConceded: number;
  totalPlayTime: number; // seconds
  highestScore: number;
}

export interface Preferences {
  masterVolume: number;
  sfxVolume: number;
  musicVolume: number;
  commentaryVolume: number;
  showRadar: boolean;
  showControls: boolean;
  showPossession: boolean;
  showShotsOnTarget: boolean;
  colorBlindMode: boolean;
  language: string;
  keyBindings: Record<string, string>;
  weather: "none" | "rain" | "snow";
  matchDuration: number; // minutes
  difficulty: string;
  fullscreen: boolean;
  frameRateLimit: number; // 0 = unlimited
  highPerformanceCrowd: boolean;
}

export interface SaveData {
  version: string;
  preferences: Preferences;
  playerStats: PlayerStats;
  matchHistory: MatchRecord[];
  achievements: string[];
  leaderboard: LeaderboardEntry[];
  localFriends: string[];
}

export interface LeaderboardEntry {
  name: string;
  score: number;
  wins: number;
  date: string;
}

const SAVE_KEY = "amnfootball_save";
const SAVE_VERSION = "1.0.0";

const DEFAULT_PREFERENCES: Preferences = {
  masterVolume: 0.8,
  sfxVolume: 1.0,
  musicVolume: 0.6,
  commentaryVolume: 0.9,
  showRadar: true,
  showControls: true,
  showPossession: true,
  showShotsOnTarget: true,
  colorBlindMode: false,
  language: "en",
  keyBindings: {
    forward: "KeyW",
    backward: "KeyS",
    left: "KeyA",
    right: "KeyD",
    sprint: "ShiftLeft",
    shoot: "Space",
    throughPass: "KeyE",
    lobKick: "KeyQ",
    tackle: "KeyF",
    pause: "Escape",
    autoPass: "KeyR",
  },
  weather: "none",
  matchDuration: 3, // 3 minutes
  difficulty: "EASY",
  fullscreen: false,
  frameRateLimit: 0,
  highPerformanceCrowd: true,
};

const DEFAULT_STATS: PlayerStats = {
  gamesPlayed: 0,
  wins: 0,
  losses: 0,
  draws: 0,
  goalsScored: 0,
  goalsConceded: 0,
  totalPlayTime: 0,
  highestScore: 0,
};

export class SaveSystem {
  private static _data: SaveData | null = null;

  // ── Load / Save ───────────────────────────────────────────────────

  static load(): SaveData {
    if (this._data) return this._data;

    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (!raw) {
        this._data = this._createDefault();
        return this._data;
      }

      const parsed = JSON.parse(raw) as SaveData;

      // Version migration / corruption check
      if (!parsed || typeof parsed !== "object") {
        console.warn("SaveSystem: Corrupted save data, resetting.");
        this._data = this._createDefault();
        return this._data;
      }

      // Merge with defaults to handle missing fields from older saves
      this._data = {
        version: SAVE_VERSION,
        preferences: { ...DEFAULT_PREFERENCES, ...(parsed.preferences || {}) },
        playerStats: { ...DEFAULT_STATS, ...(parsed.playerStats || {}) },
        matchHistory: Array.isArray(parsed.matchHistory) ? parsed.matchHistory.slice(-50) : [],
        achievements: Array.isArray(parsed.achievements) ? parsed.achievements : [],
        leaderboard: Array.isArray(parsed.leaderboard) ? parsed.leaderboard : [],
        localFriends: Array.isArray(parsed.localFriends) ? parsed.localFriends : [],
      };

      return this._data;
    } catch (e) {
      console.error("SaveSystem: Failed to load save, resetting.", e);
      this._data = this._createDefault();
      return this._data;
    }
  }

  static save(): void {
    if (!this._data) return;
    try {
      const json = JSON.stringify(this._data);
      localStorage.setItem(SAVE_KEY, json);
    } catch (e) {
      console.error("SaveSystem: Failed to save.", e);
    }
  }

  static reset(): void {
    this._data = this._createDefault();
    localStorage.removeItem(SAVE_KEY);
  }

  private static _createDefault(): SaveData {
    return {
      version: SAVE_VERSION,
      preferences: { ...DEFAULT_PREFERENCES },
      playerStats: { ...DEFAULT_STATS },
      matchHistory: [],
      achievements: [],
      leaderboard: [],
      localFriends: [],
    };
  }

  // ── Preferences ───────────────────────────────────────────────────

  static getPreferences(): Preferences {
    return this.load().preferences;
  }

  static setPreference<K extends keyof Preferences>(key: K, value: Preferences[K]): void {
    const data = this.load();
    data.preferences[key] = value;
    this.save();
  }

  static getKeyBinding(action: string): string {
    const prefs = this.getPreferences();
    return prefs.keyBindings[action] || DEFAULT_PREFERENCES.keyBindings[action] || "";
  }

  static setKeyBinding(action: string, code: string): void {
    const data = this.load();
    data.preferences.keyBindings[action] = code;
    this.save();
  }

  // ── Match History & Stats ─────────────────────────────────────────

  static recordMatch(record: MatchRecord): void {
    const data = this.load();
    data.matchHistory.push(record);
    // Keep only last 50 matches
    if (data.matchHistory.length > 50) {
      data.matchHistory = data.matchHistory.slice(-50);
    }

    // Update stats
    data.playerStats.gamesPlayed++;
    data.playerStats.goalsScored += record.playerScore;
    data.playerStats.goalsConceded += record.opponentScore;
    data.playerStats.totalPlayTime += record.duration;
    if (record.playerScore > data.playerStats.highestScore) {
      data.playerStats.highestScore = record.playerScore;
    }
    if (record.result === "win") data.playerStats.wins++;
    else if (record.result === "loss") data.playerStats.losses++;
    else data.playerStats.draws++;

    this.save();
    this._checkAchievements(data);
  }

  static getStats(): PlayerStats {
    return { ...this.load().playerStats };
  }

  static getMatchHistory(): MatchRecord[] {
    return [...this.load().matchHistory];
  }

  // ── Achievements ─────────────────────────────────────────────────

  static getAchievements(): string[] {
    return [...this.load().achievements];
  }

  static unlockAchievement(id: string): boolean {
    const data = this.load();
    if (data.achievements.includes(id)) return false;
    data.achievements.push(id);
    this.save();
    return true;
  }

  private static _checkAchievements(data: SaveData): void {
    const stats = data.playerStats;
    const achievementChecks: Array<{ id: string; condition: boolean }> = [
      { id: "first_win", condition: stats.wins >= 1 },
      { id: "hat_trick", condition: data.matchHistory.some(m => m.playerScore >= 3) },
      { id: "clean_sheet", condition: data.matchHistory.some(m => m.opponentScore === 0 && m.result === "win") },
      { id: "veteran", condition: stats.gamesPlayed >= 10 },
      { id: "legend", condition: stats.wins >= 50 },
      { id: "goal_machine", condition: stats.goalsScored >= 100 },
      { id: "unbeaten_run", condition: stats.losses === 0 && stats.gamesPlayed >= 5 },
      { id: "marathon", condition: stats.totalPlayTime >= 3600 },
    ];

    achievementChecks.forEach(({ id, condition }) => {
      if (condition && !data.achievements.includes(id)) {
        data.achievements.push(id);
        console.log(`Achievement unlocked: ${id}`);
      }
    });
  }

  // ── Leaderboard (task 5.15) ───────────────────────────────────────

  static addLeaderboardEntry(name: string, score: number, wins: number): void {
    const data = this.load();
    data.leaderboard.push({
      name,
      score,
      wins,
      date: new Date().toLocaleDateString(),
    });
    // Sort by score descending, keep top 20
    data.leaderboard.sort((a, b) => b.score - a.score);
    if (data.leaderboard.length > 20) {
      data.leaderboard = data.leaderboard.slice(0, 20);
    }
    this.save();
  }

  static getLeaderboard(): LeaderboardEntry[] {
    return [...this.load().leaderboard];
  }

  // ── Local Friends (task 15.4) ─────────────────────────────────────

  static addFriend(name: string): void {
    const data = this.load();
    if (!data.localFriends.includes(name)) {
      data.localFriends.push(name);
      this.save();
    }
  }

  static removeFriend(name: string): void {
    const data = this.load();
    data.localFriends = data.localFriends.filter(f => f !== name);
    this.save();
  }

  static getFriends(): string[] {
    return [...this.load().localFriends];
  }

  // ── Screenshot capture (task 15.1) ───────────────────────────────

  static captureScreenshot(canvas: HTMLCanvasElement): void {
    try {
      const dataURL = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataURL;
      a.download = `amnfootball_screenshot_${Date.now()}.png`;
      a.click();
    } catch (e) {
      console.error("Screenshot failed:", e);
    }
  }

  // ── Export / Import (task 15.3) ───────────────────────────────────

  static exportData(): void {
    const data = this.load();
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `amnfootball_export_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  static importData(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const parsed = JSON.parse(e.target?.result as string) as SaveData;
          this._data = {
            version: SAVE_VERSION,
            preferences: { ...DEFAULT_PREFERENCES, ...(parsed.preferences || {}) },
            playerStats: { ...DEFAULT_STATS, ...(parsed.playerStats || {}) },
            matchHistory: Array.isArray(parsed.matchHistory) ? parsed.matchHistory.slice(-50) : [],
            achievements: Array.isArray(parsed.achievements) ? parsed.achievements : [],
            leaderboard: Array.isArray(parsed.leaderboard) ? parsed.leaderboard : [],
            localFriends: Array.isArray(parsed.localFriends) ? parsed.localFriends : [],
          };
          this.save();
          resolve();
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }
}
